import React, { useState, useEffect, useRef, useCallback } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import SOPs from './SOPs';

// ── Greeting ────────────────────────────────────────────────────────────────
function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  return 'Good Evening';
}

// ── Section icon map ─────────────────────────────────────────────────────────
const SECTION_ICONS = {
  'Front Matter': '📜',
  'The Foundation': '🏛',
  'The Seven Pillars': '⭐',
  'The Bloans Way': '🛤',
  'Rule of Life': '🌿',
  'Leadership Standard': '👑',
  'Legacy': '🕊',
  'Employment & Protection Policies': '📋',
  'The Sayings of Bloans': '💬',
  'Revision History': '📝',
  'SOP Library': '📚',
  'Forms & Templates': '📝',
};
const PRIMARY_SECTIONS = ['Front Matter','The Foundation','The Seven Pillars','The Bloans Way','Rule of Life','Leadership Standard','Legacy'];

// ── Rich Text Editor ─────────────────────────────────────────────────────────
function RichEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (editorRef.current && !loaded) {
      editorRef.current.innerHTML = value || '';
      setLoaded(true);
    }
  }, [value, loaded]);

  const exec = (cmd, val = null) => {
    editorRef.current.focus();
    document.execCommand(cmd, false, val);
  };

  const btn = (label, cmd, val = null, title = '') => (
    <button type="button" title={title||label}
      onMouseDown={e => { e.preventDefault(); exec(cmd, val); }}
      style={{padding:'4px 8px',border:'1px solid #ddd',borderRadius:4,
        background:'#fff',cursor:'pointer',fontSize:12,marginRight:2,fontWeight:cmd==='bold'?700:400}}>
      {label}
    </button>
  );

  return (
    <div style={{border:'1px solid #ddd',borderRadius:8,overflow:'hidden'}}>
      <div style={{background:'#F2EFE7',padding:'6px 10px',borderBottom:'1px solid #ddd',
        display:'flex',flexWrap:'wrap',gap:2,alignItems:'center'}}>
        {btn('B','bold',null,'Bold')}
        {btn('I','italic',null,'Italic')}
        {btn('U','underline',null,'Underline')}
        <div style={{width:1,height:20,background:'#ccc',margin:'0 4px'}}/>
        {btn('H1','formatBlock','h2','Heading 1')}
        {btn('H2','formatBlock','h3','Heading 2')}
        {btn('¶','formatBlock','p','Paragraph')}
        <div style={{width:1,height:20,background:'#ccc',margin:'0 4px'}}/>
        {btn('• List','insertUnorderedList',null,'Bullet List')}
        {btn('1. List','insertOrderedList',null,'Numbered List')}
        <div style={{width:1,height:20,background:'#ccc',margin:'0 4px'}}/>
        {btn('↩ Clear','removeFormat',null,'Clear Formatting')}
      </div>
      <div ref={editorRef} contentEditable suppressContentEditableWarning
        onInput={e => onChange(e.currentTarget.innerHTML)}
        style={{minHeight:320,padding:20,fontSize:15,lineHeight:1.9,
          color:'#3B2A21',outline:'none',fontFamily:'Georgia,serif'}}
      />
    </div>
  );
}

// ── Format duration ───────────────────────────────────────────────────────────
function fmtTime(mins) {
  if (!mins) return '—';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

// ════════════════════════════════════════════════════════════════════════════
export default function BloansStandard({ initialView = 'home' }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [subChapters, setSubChapters] = useState([]);
  const [revisionHistory, setRevisionHistory] = useState(null);
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [selectedSubChapter, setSelectedSubChapter] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioPaused, setAudioPaused] = useState(false);
  const [audioSpeed, setAudioSpeed] = useState(1);
  const [audioText, setAudioText] = useState('');
  const utteranceRef = React.useRef(null);
  const [showSubEditor, setShowSubEditor] = useState(false);
  const [editingSubChapter, setEditingSubChapter] = useState(null);
  const [subEditorForm, setSubEditorForm] = useState({title:'',content:'',read_time_minutes:3,sort_order:0});
  const [myProgress, setMyProgress] = useState(null);
  const [myBookmarks, setMyBookmarks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [activeView, setActiveView] = useState(initialView);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recentlyUpdated, setRecentlyUpdated] = useState([]);

  // Editor
  const [showEditor, setShowEditor] = useState(false);
  const [editingChapter, setEditingChapter] = useState(null);
  const [editorForm, setEditorForm] = useState({
    title:'', content:'', read_time_minutes:5,
    is_mandatory:false, tags:'', sort_order:0, version_notes:''
  });
  const [editorSubChapters, setEditorSubChapters] = useState([]);
  const [editorSubForm, setEditorSubForm] = useState({title:'',content:'',read_time_minutes:3});
  const [showEditorSubForm, setShowEditorSubForm] = useState(false);
  const [editingEditorSub, setEditingEditorSub] = useState(null);

  // Section editor
  const [showSectionEditor, setShowSectionEditor] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm, setSectionForm] = useState({title:'',description:'',icon:'📄',sort_order:0});

  // Breadcrumb
  const [breadcrumb, setBreadcrumb] = useState([]);

  const load = () => {
    setLoading(true);
    Promise.all([
      get('/bloans-standard/sections'),
      get('/bloans-standard/my/progress'),
      get('/bloans-standard/my/bookmarks'),
    ]).then(([s, p, b]) => {
      setSections(s.data||[]);
      setMyProgress(p);
      setMyBookmarks(b.data||[]);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const loadChapters = (section) => {
    setSelectedSection(section);
    setSelectedChapter(null);

    // BLOANS STANDARD CONTROLLED SOP LIBRARY
    if (section.title === 'SOP Library') {
      setActiveView('sop_library');
      setBreadcrumb([
        {label:'Home',view:'home'},
        {
          label:'SOP Library',
          view:'sop_library'
        }
      ]);
      return;
    }

    setActiveView('section');
    setBreadcrumb([{label:'Home',view:'home'},{label:section.title,view:'section'}]);
    get('/bloans-standard/sections/'+section.id+'/chapters').then(d => {
      setChapters(d.data||[]);
      // Collect recently updated
      const recent = (d.data||[]).filter(c=>c.updated_at).sort((a,b)=>new Date(b.updated_at)-new Date(a.updated_at)).slice(0,3);
      setRecentlyUpdated(recent);
    });
  };

  const loadChapter = (chapter, section) => {
    setSelectedChapter(chapter);
    setSubChapters([]);
    setSelectedSubChapter(null);
    setActiveView('chapter');
    const sec = section || selectedSection;
    setBreadcrumb([
      {label:'Home',view:'home'},
      {label:sec?.title||'',view:'section'},
      {label:chapter.title,view:'chapter'}
    ]);
    post('/bloans-standard/chapters/'+chapter.id+'/progress',{read_percentage:10,time_spent_seconds:0}).catch(()=>{});
    get('/bloans-standard/chapters/'+chapter.id+'/sub-chapters').then(d=>setSubChapters(d.data||[]));
  };

  const navigateBreadcrumb = (crumb) => {
    if (crumb.view === 'home') { setActiveView('home'); setBreadcrumb([]); }
    else if (crumb.view === 'section') { setActiveView('section'); setBreadcrumb(breadcrumb.slice(0,2)); }
  };

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const result = await get('/bloans-standard/search?q='+encodeURIComponent(q));
      setSearchResults(result.data||[]);
    } finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery, search]);

  const acknowledge = async (chapterId) => {
    try {
      await post('/bloans-standard/chapters/'+chapterId+'/acknowledge', {});
      const updated = await get('/bloans-standard/chapters/'+chapterId);
      setSelectedChapter(updated.data);
      load();
    } catch(err) { alert(err.message); }
  };

  const toggleBookmark = async (chapterId) => {
    try {
      await post('/bloans-standard/chapters/'+chapterId+'/bookmark', {});
      const updated = await get('/bloans-standard/chapters/'+chapterId);
      setSelectedChapter(updated.data);
      get('/bloans-standard/my/bookmarks').then(d => setMyBookmarks(d.data||[]));
    } catch(err) { alert(err.message); }
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html || '';
    return tmp.textContent || tmp.innerText || '';
  };

  const startAudio = (text) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = audioSpeed;
    utterance.lang = 'en-NG';
    utterance.onend = () => { setAudioPlaying(false); setAudioPaused(false); };
    utterance.onerror = () => { setAudioPlaying(false); setAudioPaused(false); };
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
    setAudioPlaying(true);
    setAudioPaused(false);
  };

  const playAudio = (chapter) => {
    const text = chapter.title + '. ' + stripHtml(chapter.content);
    setAudioText(text);
    startAudio(text);
  };

  const pauseAudio = () => {
    window.speechSynthesis.pause();
    setAudioPaused(true);
    setAudioPlaying(false);
  };

  const resumeAudio = () => {
    window.speechSynthesis.resume();
    setAudioPlaying(true);
    setAudioPaused(false);
  };

  const stopAudio = () => {
    window.speechSynthesis.cancel();
    setAudioPlaying(false);
    setAudioPaused(false);
  };

  const changeSpeed = (speed) => {
    setAudioSpeed(speed);
    if (audioPlaying || audioPaused) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(audioText);
      utterance.rate = speed;
      utterance.lang = 'en-NG';
      utterance.onend = () => { setAudioPlaying(false); setAudioPaused(false); };
      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
      setAudioPlaying(true);
      setAudioPaused(false);
    }
  };

  const saveSubChapter = async () => {
    setSaving(true);
    try {
      if (editingSubChapter) {
        await put('/bloans-standard/sub-chapters/'+editingSubChapter.id, subEditorForm);
      } else {
        await post('/bloans-standard/chapters/'+selectedChapter.id+'/sub-chapters', subEditorForm);
      }
      setShowSubEditor(false);
      setEditingSubChapter(null);
      get('/bloans-standard/chapters/'+selectedChapter.id+'/sub-chapters').then(d=>setSubChapters(d.data||[]));
      return true;
    } catch(err) { alert(err.message); return false; }
    finally { setSaving(false); }
  };

  const markSubChapterRead = async (subId) => {
    try {
      await post('/bloans-standard/sub-chapters/'+subId+'/read', {});
      get('/bloans-standard/chapters/'+selectedChapter.id+'/sub-chapters').then(d=>setSubChapters(d.data||[]));
    } catch(err) { console.error(err); }
  };

  const saveChapter = async () => {
    setSaving(true);
    try {
      let chapterId;
      if (editingChapter) {
        await put('/bloans-standard/chapters/'+editingChapter.id, {
          ...editorForm,
          tags: editorForm.tags ? editorForm.tags.split(',').map(t=>t.trim()) : []
        });
        chapterId = editingChapter.id;
      } else {
        const result = await post('/bloans-standard/sections/'+selectedSection.id+'/chapters', {
          ...editorForm,
          tags: editorForm.tags ? editorForm.tags.split(',').map(t=>t.trim()) : []
        });
        chapterId = result.id;
      }
      // Save new sub-chapters that don't have an id yet
      for (let i = 0; i < editorSubChapters.length; i++) {
        const sc = editorSubChapters[i];
        if (!sc.id) {
          await post('/bloans-standard/chapters/'+chapterId+'/sub-chapters', {
            title: sc.title,
            content: sc.content||'',
            read_time_minutes: sc.read_time_minutes||3,
            sort_order: sc.sort_order||i+1
          }).catch(()=>{});
        } else {
          await put('/bloans-standard/sub-chapters/'+sc.id, {
            title: sc.title,
            content: sc.content||'',
            read_time_minutes: sc.read_time_minutes||3,
            sort_order: sc.sort_order||i+1
          }).catch(()=>{});
        }
      }
      setShowEditor(false);
      setEditingChapter(null);
      setEditorSubChapters([]);
      get('/bloans-standard/sections/'+selectedSection.id+'/chapters').then(d=>setChapters(d.data||[]));
      return chapterId;
    } catch(err) { alert(err.message); return false; }
    finally { setSaving(false); }
  };

  const publishChapter = async (id) => {
    try {
      await put('/bloans-standard/chapters/'+id+'/publish', {});
      if (selectedSection) get('/bloans-standard/sections/'+selectedSection.id+'/chapters').then(d=>setChapters(d.data||[]));
      if (selectedChapter?.id === id) {
        const updated = await get('/bloans-standard/chapters/'+id);
        setSelectedChapter(updated.data);
      }
    } catch(err) { alert(err.message); }
  };

  const deleteChapter = async (id) => {
    if (!window.confirm('Delete this chapter?')) return;
    try {
      await del('/bloans-standard/chapters/'+id);
      setSelectedChapter(null);
      setActiveView('section');
      get('/bloans-standard/sections/'+selectedSection.id+'/chapters').then(d=>setChapters(d.data||[]));
    } catch(err) { alert(err.message); }
  };

  const saveSection = async () => {
    setSaving(true);
    try {
      if (editingSection) {
        await put('/bloans-standard/sections/'+editingSection.id, sectionForm);
      } else {
        await post('/bloans-standard/sections', sectionForm);
      }
      setShowSectionEditor(false);
      setEditingSection(null);
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  // Progress stats
  const total = myProgress?.total || 0;
  const completed = myProgress?.completed || 0;
  const inProgressCount = myProgress?.in_progress?.length || 0;
  const remaining = Math.max(0, total - completed - inProgressCount);
  const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Split sections
  const primarySections = sections.filter(s => PRIMARY_SECTIONS.includes(s.title));
  const supportSections = sections.filter(s => !PRIMARY_SECTIONS.includes(s.title));

  // Quote of the day (rotates daily)
  const quotes = [
    'Rest is not a requirement for finished work. It is a requirement for sustainable work.',
    'Integrity is not what you do when people are watching. It is what you do when no one is.',
    'The standard is not what we aim for. It is what we refuse to fall below.',
    'A Steward does not own the work. A Steward protects it for those who come next.',
    'Excellence is not an event. It is a daily decision.',
  ];
  const quote = quotes[new Date().getDate() % quotes.length];

  // ── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div style={{display:'flex',height:'calc(100vh - 120px)',background:'#F9F8F5'}}>

      {/* ── LEFT SIDEBAR ─────────────────────────────────────────────────── */}
      <div style={{width:240,flexShrink:0,background:'#2C1F16',overflowY:'auto',
        display:'flex',flexDirection:'column',borderRight:'1px solid rgba(255,255,255,.06)'}}>

        {/* Logo */}
        <div style={{padding:'24px 20px 16px',borderBottom:'1px solid rgba(255,255,255,.08)'}}>
          <div style={{fontSize:9,color:'rgba(242,239,231,.35)',textTransform:'uppercase',
            letterSpacing:'.15em',marginBottom:6}}>Knowledge</div>
          <div style={{fontSize:16,fontWeight:700,color:'#C9A96E',
            fontFamily:'Cormorant Garamond,serif',letterSpacing:'.02em'}}>
            Bloans Standard
          </div>
        </div>

        {/* Search */}
        <div style={{padding:'12px 16px',borderBottom:'1px solid rgba(255,255,255,.06)'}}>
          <div style={{position:'relative'}}>
            <span style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',
              fontSize:12,color:'rgba(242,239,231,.3)'}}>🔍</span>
            <input placeholder="Search the Standard..."
              value={searchQuery}
              onChange={e=>setSearchQuery(e.target.value)}
              onFocus={()=>setActiveView('search')}
              style={{width:'100%',background:'rgba(255,255,255,.07)',border:'none',
                borderRadius:6,padding:'7px 10px 7px 28px',fontSize:11,
                color:'#F2EFE7',outline:'none',boxSizing:'border-box'}}
            />
          </div>
        </div>

        {/* Progress */}
        <div style={{padding:'14px 16px',borderBottom:'1px solid rgba(255,255,255,.06)'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:10}}>
            {[
              ['Completed',completed,'#A8893D'],
              ['Reading',inProgressCount,'#6B8F71'],
              ['Remaining',remaining,'rgba(242,239,231,.4)'],
              ['Progress',progressPct+'%','#C9A96E'],
            ].map(([label,val,color])=>(
              <div key={label} style={{background:'rgba(255,255,255,.05)',
                borderRadius:6,padding:'8px 10px'}}>
                <div style={{fontSize:9,color:'rgba(242,239,231,.4)',
                  textTransform:'uppercase',letterSpacing:'.08em',marginBottom:3}}>{label}</div>
                <div style={{fontSize:16,fontWeight:700,color}}>{val}</div>
              </div>
            ))}
          </div>
          <div style={{height:3,background:'rgba(255,255,255,.1)',borderRadius:2}}>
            <div style={{height:3,width:progressPct+'%',
              background:'linear-gradient(90deg,#A8893D,#C9A96E)',borderRadius:2,
              transition:'width .5s ease'}}/>
          </div>
          <div style={{fontSize:9,color:'rgba(242,239,231,.3)',marginTop:4,textAlign:'center'}}>
            {total} chapters total
          </div>
        </div>

        {/* Nav */}
        <div style={{flex:1,overflowY:'auto',padding:'8px 0'}}>
          {[
            {view:'home',icon:'🏠',label:'Home'},
            {view:'bookmarks',icon:'🔖',label:`Bookmarks (${myBookmarks.length})`},
          ].map(item=>(
            <div key={item.view} onClick={()=>{setActiveView(item.view);setBreadcrumb([]);}}
              style={{padding:'9px 20px',cursor:'pointer',fontSize:12,
                display:'flex',alignItems:'center',gap:8,
                color:activeView===item.view?'#C9A96E':'rgba(242,239,231,.6)',
                background:activeView===item.view?'rgba(201,169,110,.12)':'transparent',
                borderLeft:activeView===item.view?'2px solid #C9A96E':'2px solid transparent'}}>
              <span>{item.icon}</span><span>{item.label}</span>
            </div>
          ))}

          <div style={{padding:'10px 20px 4px',fontSize:8,color:'rgba(242,239,231,.25)',
            textTransform:'uppercase',letterSpacing:'.12em',marginTop:6}}>Primary</div>

          {primarySections.map(s=>{
            const icon = SECTION_ICONS[s.title] || s.icon || '📄';
            const active = selectedSection?.id===s.id;
            return (
              <div key={s.id} onClick={()=>loadChapters(s)}
                style={{padding:'9px 20px',cursor:'pointer',fontSize:12,
                  display:'flex',alignItems:'center',justifyContent:'space-between',
                  color:active?'#C9A96E':'rgba(242,239,231,.75)',
                  background:active?'rgba(201,169,110,.12)':'transparent',
                  borderLeft:active?'2px solid #C9A96E':'2px solid transparent'}}>
                <span>{icon} {s.title}</span>
                <span style={{fontSize:10,color:'rgba(242,239,231,.3)'}}>{s.chapter_count}</span>
              </div>
            );
          })}

          <div style={{padding:'10px 20px 4px',fontSize:8,color:'rgba(242,239,231,.25)',
            textTransform:'uppercase',letterSpacing:'.12em',marginTop:6}}>Resources</div>

          {supportSections.map(s=>{
            const icon = SECTION_ICONS[s.title] || s.icon || '📄';
            const active = selectedSection?.id===s.id;
            return (
              <div key={s.id} onClick={()=>loadChapters(s)}
                style={{padding:'9px 20px',cursor:'pointer',fontSize:12,
                  display:'flex',alignItems:'center',justifyContent:'space-between',
                  color:active?'#C9A96E':'rgba(242,239,231,.55)',
                  background:active?'rgba(201,169,110,.12)':'transparent',
                  borderLeft:active?'2px solid #C9A96E':'2px solid transparent'}}>
                <span>{icon} {s.title}</span>
                <span style={{fontSize:10,color:'rgba(242,239,231,.3)'}}>{s.chapter_count}</span>
              </div>
            );
          })}

          {isSuperAdmin && (
            <div onClick={()=>{setEditingSection(null);setSectionForm({title:'',description:'',icon:'📄',sort_order:sections.length+1});setShowSectionEditor(true);}}
              style={{padding:'9px 20px',cursor:'pointer',fontSize:11,
                color:'rgba(201,169,110,.5)',marginTop:8,
                borderLeft:'2px solid transparent'}}>
              + Add Section
            </div>
          )}
        </div>
      </div>

      {/* ── MAIN CONTENT ─────────────────────────────────────────────────── */}
      <div style={{flex:1,overflowY:'auto'}}>

        {/* ── SOP LIBRARY ─────────────────────────────────────────────── */}
        {activeView==='sop_library' && (
          <div style={{
            minHeight:'100%',
            background:'#F9F8F5',
            padding:'28px 32px'
          }}>
            <SOPs embedded />
          </div>
        )}

        {/* ── HOME ─────────────────────────────────────────────────────── */}
        {activeView==='home' && (
          <div>
            {/* Hero */}
            <div style={{background:'linear-gradient(135deg,#2C1F16 0%,#3B2A21 60%,#4A3428 100%)',
              padding:'48px 48px 40px',position:'relative',overflow:'hidden'}}>
              <div style={{position:'absolute',top:0,right:0,width:300,height:300,
                background:'radial-gradient(circle,rgba(168,137,61,.15) 0%,transparent 70%)',
                pointerEvents:'none'}}/>
              <div style={{fontSize:10,color:'rgba(242,239,231,.35)',textTransform:'uppercase',
                letterSpacing:'.2em',marginBottom:12}}>The Bloans Standard</div>
              <h1 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'2.8rem',
                color:'#F2EFE7',margin:'0 0 6px',fontWeight:400,letterSpacing:'.01em'}}>
                {getGreeting()}, {user?.name?.split(' ')[0]}
              </h1>
              <p style={{color:'rgba(242,239,231,.5)',fontSize:14,margin:0}}>
                Every Steward is shaped by what they consistently return to.
              </p>

              {/* Continue Reading */}
              {myProgress?.in_progress?.length > 0 && (
                <div style={{marginTop:28,background:'rgba(255,255,255,.07)',
                  borderRadius:12,padding:'16px 20px',display:'inline-flex',
                  alignItems:'center',gap:20,cursor:'pointer',border:'1px solid rgba(201,169,110,.2)'}}
                  onClick={()=>{
                    const ch = myProgress.in_progress[0];
                    get('/bloans-standard/chapters/'+ch.id).then(d=>{
                      setSelectedChapter(d.data);
                      setActiveView('chapter');
                      setBreadcrumb([{label:'Home',view:'home'},{label:ch.section_title,view:'section'},{label:ch.title,view:'chapter'}]);
                    });
                  }}>
                  <div>
                    <div style={{fontSize:9,color:'rgba(242,239,231,.4)',
                      textTransform:'uppercase',letterSpacing:'.1em',marginBottom:4}}>
                      Continue Reading
                    </div>
                    <div style={{fontSize:15,fontWeight:600,color:'#F2EFE7',
                      fontFamily:'Cormorant Garamond,serif'}}>
                      {myProgress.in_progress[0].title}
                    </div>
                    <div style={{fontSize:11,color:'rgba(242,239,231,.5)',marginTop:2}}>
                      {myProgress.in_progress[0].section_title}
                    </div>
                  </div>
                  <div style={{textAlign:'center'}}>
                    <div style={{fontSize:20,fontWeight:700,color:'#C9A96E'}}>
                      {myProgress.in_progress[0].read_percentage}%
                    </div>
                    <div style={{width:50,height:4,background:'rgba(255,255,255,.15)',
                      borderRadius:2,marginTop:4}}>
                      <div style={{width:myProgress.in_progress[0].read_percentage+'%',
                        height:4,background:'#C9A96E',borderRadius:2}}/>
                    </div>
                    <div style={{fontSize:10,color:'#C9A96E',marginTop:6,fontWeight:600}}>
                      Continue →
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{padding:'40px 48px'}}>

              {/* Primary Sections — visually dominant */}
              <div style={{marginBottom:40}}>
                <div style={{fontSize:10,fontWeight:600,color:'#A8893D',
                  textTransform:'uppercase',letterSpacing:'.12em',marginBottom:20}}>
                  The Foundation
                </div>
                {primarySections.map((s,i)=>(
                  <div key={s.id} onClick={()=>loadChapters(s)}
                    style={{background:'#fff',border:'1px solid #E8E0D4',
                      borderRadius:12,padding:'24px 28px',marginBottom:12,
                      cursor:'pointer',display:'flex',justifyContent:'space-between',
                      alignItems:'center',transition:'all .2s',
                      borderLeft:'4px solid #A8893D'}}>
                    <div>
                      <div style={{fontSize:22,marginBottom:8}}>
                        {SECTION_ICONS[s.title]||s.icon}
                      </div>
                      <div style={{fontSize:18,fontWeight:600,color:'#2C1F16',
                        fontFamily:'Cormorant Garamond,serif',marginBottom:4}}>
                        {s.title}
                      </div>
                      <div style={{fontSize:12,color:'#8A8A8A'}}>{s.description}</div>
                    </div>
                    <div style={{textAlign:'right',flexShrink:0,marginLeft:24}}>
                      <div style={{fontSize:11,color:'#A8893D',fontWeight:600,marginBottom:4}}>
                        {s.chapter_count} chapters
                      </div>
                      <div style={{fontSize:12,color:'#556B2F',fontWeight:600}}>Read →</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Featured + Quote row */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:40}}>

                {/* Quote of the Day */}
                <div style={{background:'linear-gradient(135deg,#3B2A21,#2C1F16)',
                  borderRadius:12,padding:24,display:'flex',flexDirection:'column',
                  justifyContent:'space-between'}}>
                  <div style={{fontSize:9,color:'rgba(242,239,231,.35)',
                    textTransform:'uppercase',letterSpacing:'.12em',marginBottom:12}}>
                    Principle of the Day
                  </div>
                  <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:17,
                    color:'#F2EFE7',lineHeight:1.6,fontStyle:'italic',flex:1}}>
                    "{quote}"
                  </div>
                  <div style={{marginTop:16,fontSize:10,color:'rgba(242,239,231,.3)'}}>
                    Bloans Standard
                  </div>
                </div>

                {/* Recently Updated */}
                <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,padding:24}}>
                  <div style={{fontSize:9,fontWeight:600,color:'#556B2F',
                    textTransform:'uppercase',letterSpacing:'.12em',marginBottom:12}}>
                    Recently Updated
                  </div>
                  {recentlyUpdated.length === 0 ? (
                    <div style={{fontSize:12,color:'#8A8A8A'}}>No updates yet</div>
                  ) : recentlyUpdated.map(ch=>(
                    <div key={ch.id} style={{display:'flex',justifyContent:'space-between',
                      alignItems:'center',padding:'8px 0',borderBottom:'1px solid #F2EFE7',
                      cursor:'pointer'}}
                      onClick={()=>{ get('/bloans-standard/chapters/'+ch.id).then(d=>{ setSelectedChapter(d.data); setActiveView('chapter'); }); }}>
                      <div>
                        <div style={{fontSize:12,fontWeight:600,color:'#3B2A21'}}>{ch.title}</div>
                        <div style={{fontSize:10,color:'#8A8A8A'}}>v{ch.version}</div>
                      </div>
                      <div style={{fontSize:10,color:'#556B2F'}}>→</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Supporting Resources */}
              <div>
                <div style={{fontSize:10,fontWeight:600,color:'#8A8A8A',
                  textTransform:'uppercase',letterSpacing:'.12em',marginBottom:16}}>
                  Supporting Resources
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:10}}>
                  {supportSections.map(s=>(
                    <div key={s.id} onClick={()=>loadChapters(s)}
                      style={{background:'#fff',border:'1px solid #E8E0D4',
                        borderRadius:10,padding:'16px 20px',cursor:'pointer'}}>
                      <div style={{fontSize:20,marginBottom:8}}>{SECTION_ICONS[s.title]||s.icon}</div>
                      <div style={{fontSize:13,fontWeight:600,color:'#3B2A21',marginBottom:4}}>{s.title}</div>
                      <div style={{fontSize:10,color:'#8A8A8A'}}>{s.chapter_count} chapters</div>
                    </div>
                  ))}
                  {isSuperAdmin && (
                    <div onClick={()=>{setEditingSection(null);setSectionForm({title:'',description:'',icon:'📄',sort_order:sections.length+1});setShowSectionEditor(true);}}
                      style={{background:'#F9F8F5',border:'1px dashed #ccc',
                        borderRadius:10,padding:'16px 20px',cursor:'pointer',
                        display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <span style={{fontSize:12,color:'#8A8A8A'}}>+ Add Section</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── SEARCH ───────────────────────────────────────────────────── */}
        {activeView==='search' && (
          <div style={{padding:40,maxWidth:720}}>
            <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem',
              color:'#2C1F16',marginBottom:20}}>Search Results</h2>
            {searching && <div className="spinner"/>}
            {!searching && searchQuery.length > 1 && searchResults.length === 0 && (
              <div style={{color:'#8A8A8A',fontSize:13,fontStyle:'italic'}}>
                No results found for "{searchQuery}"
              </div>
            )}
            {searchResults.map(r=>(
              <div key={r.id} style={{background:'#fff',border:'1px solid #E8E0D4',
                borderRadius:10,padding:20,marginBottom:10,cursor:'pointer'}}
                onClick={()=>{ get('/bloans-standard/chapters/'+r.id).then(d=>{setSelectedChapter(d.data);setActiveView('chapter');}); }}>
                <div style={{fontSize:14,fontWeight:600,color:'#2C1F16',marginBottom:2}}>{r.title}</div>
                <div style={{fontSize:11,color:'#A8893D',marginBottom:8}}>{r.section_title}</div>
                {r.excerpt && <div style={{fontSize:12,color:'#555',lineHeight:1.6}}
                  dangerouslySetInnerHTML={{__html:r.excerpt+'...'}}/>}
                {r.tags?.length > 0 && (
                  <div style={{marginTop:8,display:'flex',gap:4,flexWrap:'wrap'}}>
                    {r.tags.map(t=>(
                      <span key={t} style={{fontSize:9,background:'#F2EFE7',color:'#556B2F',
                        padding:'2px 8px',borderRadius:10}}>{t}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── BOOKMARKS ────────────────────────────────────────────────── */}
        {activeView==='bookmarks' && (
          <div style={{padding:40,maxWidth:700}}>
            <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem',
              color:'#2C1F16',marginBottom:20}}>🔖 Bookmarks</h2>
            {myBookmarks.length === 0 ? (
              <div style={{color:'#8A8A8A',fontSize:13,fontStyle:'italic'}}>
                No bookmarks yet. Bookmark chapters while reading.
              </div>
            ) : myBookmarks.map(b=>(
              <div key={b.id} style={{background:'#fff',border:'1px solid #E8E0D4',
                borderRadius:10,padding:16,marginBottom:10,cursor:'pointer',
                display:'flex',justifyContent:'space-between',alignItems:'center'}}
                onClick={()=>{ get('/bloans-standard/chapters/'+b.id).then(d=>{setSelectedChapter(d.data);setActiveView('chapter');}); }}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:'#2C1F16'}}>{b.title}</div>
                  <div style={{fontSize:11,color:'#8A8A8A'}}>{b.section_title}</div>
                </div>
                <span style={{fontSize:11,color:'#A8893D'}}>→</span>
              </div>
            ))}
          </div>
        )}

        {/* ── SECTION ──────────────────────────────────────────────────── */}
        {activeView==='section' && selectedSection && (
          <div style={{padding:40,maxWidth:800}}>
            {/* Breadcrumb */}
            <div style={{fontSize:11,color:'#8A8A8A',marginBottom:20,display:'flex',
              alignItems:'center',gap:6}}>
              {breadcrumb.map((c,i)=>(
                <span key={i} style={{display:'flex',alignItems:'center',gap:6}}>
                  {i>0 && <span style={{color:'#ccc'}}>›</span>}
                  <span style={{cursor:i<breadcrumb.length-1?'pointer':'default',
                    color:i<breadcrumb.length-1?'#556B2F':'#3B2A21',fontWeight:i===breadcrumb.length-1?600:400}}
                    onClick={()=>i<breadcrumb.length-1&&navigateBreadcrumb(c)}>
                    {c.label}
                  </span>
                </span>
              ))}
            </div>

            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:28}}>
              <div>
                <div style={{fontSize:32,marginBottom:8}}>{SECTION_ICONS[selectedSection.title]||selectedSection.icon}</div>
                <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'2rem',
                  color:'#2C1F16',margin:'0 0 6px'}}>{selectedSection.title}</h2>
                {selectedSection.description && (
                  <p style={{color:'#8A8A8A',fontSize:13,margin:0}}>{selectedSection.description}</p>
                )}
              </div>
              {isSuperAdmin && (
                <div style={{display:'flex',gap:8}}>
                  <button className="btn btn-outline btn-sm"
                    onClick={()=>{setEditingSection(selectedSection);setSectionForm({title:selectedSection.title,description:selectedSection.description||'',icon:selectedSection.icon,sort_order:selectedSection.sort_order});setShowSectionEditor(true);}}>
                    Edit
                  </button>
                  <button className="btn btn-primary btn-sm"
                    onClick={()=>{setEditingChapter(null);setEditorForm({title:'',content:'',read_time_minutes:5,is_mandatory:false,tags:'',sort_order:chapters.length+1,version_notes:''});setEditorSubChapters([]);setShowEditorSubForm(false);setEditingEditorSub(null);setShowEditor(true);}}>
                    + New Chapter
                  </button>
                </div>
              )}
            </div>

            {chapters.length === 0 ? (
              <div className="empty-state">
                <h3>No chapters yet</h3>
                {isSuperAdmin && <p>Click + New Chapter to begin</p>}
              </div>
            ) : chapters.map(ch=>(
              <div key={ch.id} onClick={()=>loadChapter(ch)}
                style={{background:'#fff',border:'1px solid #E8E0D4',
                  borderRadius:10,padding:'18px 22px',marginBottom:10,cursor:'pointer',
                  display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div style={{flex:1}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                    <span style={{fontSize:14,fontWeight:600,color:'#2C1F16'}}>{ch.title}</span>
                    {ch.status==='draft' && isSuperAdmin && (
                      <span style={{fontSize:9,background:'#FFF3DC',color:'#8B5E0A',
                        padding:'2px 8px',borderRadius:10,fontWeight:700}}>DRAFT</span>
                    )}
                    {ch.is_mandatory && (
                      <span style={{fontSize:9,background:'#FDEAEA',color:'#8F2E24',
                        padding:'2px 8px',borderRadius:10,fontWeight:700}}>REQUIRED</span>
                    )}
                    {ch.my_ack && (
                      <span style={{fontSize:9,background:'#E8F5EB',color:'#2E6B3A',
                        padding:'2px 8px',borderRadius:10,fontWeight:700}}>✓ DONE</span>
                    )}
                  </div>
                  <div style={{fontSize:11,color:'#8A8A8A',display:'flex',gap:12}}>
                    <span>⏱ {ch.read_time_minutes} min</span>
                    <span>v{ch.version}</span>
                    {ch.tags?.length > 0 && <span>{ch.tags.slice(0,2).join(', ')}</span>}
                  </div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:12,marginLeft:16,flexShrink:0}}>
                  {ch.my_progress > 0 && ch.my_progress < 100 && (
                    <div style={{textAlign:'center'}}>
                      <div style={{fontSize:11,color:'#A8893D',fontWeight:600}}>{ch.my_progress}%</div>
                      <div style={{width:40,height:3,background:'#EDE7D8',borderRadius:2,marginTop:2}}>
                        <div style={{width:ch.my_progress+'%',height:3,background:'#A8893D',borderRadius:2}}/>
                      </div>
                    </div>
                  )}
                  {ch.my_bookmark && <span style={{fontSize:14}}>🔖</span>}
                  <span style={{color:'#A8893D',fontSize:14}}>→</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── CHAPTER ──────────────────────────────────────────────────── */}
        {activeView==='chapter' && selectedChapter && (
          <div style={{padding:'40px 48px',maxWidth:780}}>
            {/* Breadcrumb */}
            <div style={{fontSize:11,color:'#8A8A8A',marginBottom:24,display:'flex',
              alignItems:'center',gap:6,flexWrap:'wrap'}}>
              {breadcrumb.map((c,i)=>(
                <span key={i} style={{display:'flex',alignItems:'center',gap:6}}>
                  {i>0 && <span style={{color:'#ccc'}}>›</span>}
                  <span style={{cursor:i<breadcrumb.length-1?'pointer':'default',
                    color:i<breadcrumb.length-1?'#556B2F':'#3B2A21',
                    fontWeight:i===breadcrumb.length-1?600:400}}
                    onClick={()=>i<breadcrumb.length-1&&navigateBreadcrumb(c)}>
                    {c.label}
                  </span>
                </span>
              ))}
            </div>

            {/* Chapter header */}
            <div style={{marginBottom:28}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:16}}>
                <h1 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'2.2rem',
                  color:'#2C1F16',margin:'0 0 10px',lineHeight:1.2,fontWeight:500}}>
                  {selectedChapter.title}
                </h1>
                <div style={{display:'flex',gap:6,flexShrink:0}}>
                  <button onClick={()=>toggleBookmark(selectedChapter.id)}
                    style={{background:'none',border:'1px solid #E8E0D4',borderRadius:6,
                      padding:'6px 10px',cursor:'pointer',fontSize:14,color:'#8A8A8A'}}>
                    {selectedChapter.my_bookmark ? '🔖' : '☆'}
                  </button>
                  {isSuperAdmin && (
                    <>
                      <button className="btn btn-outline btn-sm"
                        onClick={()=>{setEditingChapter(selectedChapter);setEditorForm({title:selectedChapter.title,content:selectedChapter.content||'',read_time_minutes:selectedChapter.read_time_minutes,is_mandatory:selectedChapter.is_mandatory,tags:(selectedChapter.tags||[]).join(', '),sort_order:selectedChapter.sort_order,version_notes:''});setShowEditor(true);}}>
                        Edit
                      </button>
                      {selectedChapter.status==='draft'
                        ? <button className="btn btn-primary btn-sm" onClick={()=>publishChapter(selectedChapter.id)}>Publish</button>
                        : <button className="btn btn-outline btn-sm" style={{color:'#8B5E0A'}}
                            onClick={()=>put('/bloans-standard/chapters/'+selectedChapter.id+'/unpublish',{}).then(()=>get('/bloans-standard/chapters/'+selectedChapter.id).then(d=>setSelectedChapter(d.data)))}>
                            Unpublish
                          </button>
                      }
                      <button className="btn btn-ghost btn-sm" style={{color:'#8F2E24'}}
                        onClick={()=>deleteChapter(selectedChapter.id)}>Delete</button>
                    </>
                  )}
                </div>
              </div>

              <div style={{display:'flex',gap:20,fontSize:11,color:'#8A8A8A',flexWrap:'wrap'}}>
                <span>⏱ {selectedChapter.read_time_minutes} min read</span>
                <span>📋 Version {selectedChapter.version}</span>
                {selectedChapter.published_at && (
                  <span>📅 {new Date(selectedChapter.published_at).toLocaleDateString('en-NG',{day:'numeric',month:'short',year:'numeric'})}</span>
                )}
                {selectedChapter.is_mandatory && (
                  <span style={{color:'#8F2E24',fontWeight:600}}>⚠ Required Reading</span>
                )}
                {selectedChapter.status==='draft' && (
                  <span style={{color:'#8B5E0A',fontWeight:600}}>Draft</span>
                )}
              </div>

              {selectedChapter.tags?.length > 0 && (
                <div style={{marginTop:10,display:'flex',gap:4,flexWrap:'wrap'}}>
                  {selectedChapter.tags.map(t=>(
                    <span key={t} style={{fontSize:9,background:'#F2EFE7',color:'#556B2F',
                      padding:'3px 10px',borderRadius:20}}>{t}</span>
                  ))}
                </div>
              )}

              {/* Version info */}
              <div style={{marginTop:12,padding:'8px 14px',background:'#F9F8F5',
                borderRadius:6,fontSize:11,color:'#8A8A8A',display:'inline-flex',gap:16}}>
                <span>Current Version: <strong style={{color:'#3B2A21'}}>v{selectedChapter.version}</strong></span>
                {selectedChapter.updated_at && (
                  <span>Updated: <strong style={{color:'#3B2A21'}}>{new Date(selectedChapter.updated_at).toLocaleDateString('en-NG',{day:'numeric',month:'short',year:'numeric'})}</strong></span>
                )}
              </div>
            </div>

            {/* Divider */}
            <div style={{height:1,background:'#E8E0D4',marginBottom:28}}/>

            {/* Audio Player */}
            {selectedChapter.content && (
              <div style={{background:'#2C1F16',borderRadius:10,padding:'12px 20px',
                marginBottom:24,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                <span style={{fontSize:11,color:'rgba(242,239,231,.5)',flexShrink:0}}>🔊 Listen</span>
                <div style={{display:'flex',gap:6,alignItems:'center'}}>
                  {!audioPlaying && !audioPaused && (
                    <button onClick={()=>playAudio(selectedChapter)}
                      style={{background:'#C9A96E',color:'#2C1F16',border:'none',
                        borderRadius:6,padding:'6px 14px',fontSize:12,cursor:'pointer',fontWeight:600}}>
                      ▶ Play
                    </button>
                  )}
                  {audioPlaying && (
                    <button onClick={pauseAudio}
                      style={{background:'#A8893D',color:'#fff',border:'none',
                        borderRadius:6,padding:'6px 14px',fontSize:12,cursor:'pointer',fontWeight:600}}>
                      ⏸ Pause
                    </button>
                  )}
                  {audioPaused && (
                    <button onClick={resumeAudio}
                      style={{background:'#C9A96E',color:'#2C1F16',border:'none',
                        borderRadius:6,padding:'6px 14px',fontSize:12,cursor:'pointer',fontWeight:600}}>
                      ▶ Resume
                    </button>
                  )}
                  {(audioPlaying || audioPaused) && (
                    <button onClick={stopAudio}
                      style={{background:'rgba(255,255,255,.1)',color:'rgba(242,239,231,.7)',border:'none',
                        borderRadius:6,padding:'6px 14px',fontSize:12,cursor:'pointer'}}>
                      ⏹ Stop
                    </button>
                  )}
                </div>
                <div style={{display:'flex',alignItems:'center',gap:4,marginLeft:'auto'}}>
                  <span style={{fontSize:10,color:'rgba(242,239,231,.4)',marginRight:4}}>Speed:</span>
                  {[0.75,1,1.25,1.5,2].map(s=>(
                    <button key={s} onClick={()=>changeSpeed(s)}
                      style={{background:audioSpeed===s?'#C9A96E':'rgba(255,255,255,.1)',
                        color:audioSpeed===s?'#2C1F16':'rgba(242,239,231,.7)',
                        border:'none',borderRadius:4,padding:'3px 8px',
                        fontSize:10,cursor:'pointer',fontWeight:audioSpeed===s?700:400}}>
                      {s}x
                    </button>
                  ))}
                </div>
                {audioPlaying && (
                  <div style={{width:'100%',fontSize:10,color:'rgba(242,239,231,.4)',
                    fontStyle:'italic',marginTop:2}}>
                    Reading: {selectedChapter.title}...
                  </div>
                )}
              </div>
            )}

            {/* Content */}
            <div style={{lineHeight:1.9,fontSize:15,color:'#2C1F16',
              fontFamily:'Georgia,serif',marginBottom:40}}
              dangerouslySetInnerHTML={{__html:selectedChapter.content||'<p style="color:#8A8A8A;font-style:italic">No content yet.</p>'}}
            />

            {/* Sub-chapters */}
            {(subChapters.length > 0 || isSuperAdmin) && (
              <div style={{marginBottom:32}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#556B2F',
                    textTransform:'uppercase',letterSpacing:'.1em'}}>
                    Sub-chapters {subChapters.length > 0 && `(${subChapters.filter(s=>s.my_read).length}/${subChapters.filter(s=>s.status==='published').length} read)`}
                  </div>
                  {isSuperAdmin && (
                    <button className="btn btn-outline btn-sm"
                      onClick={()=>{setEditingSubChapter(null);setSubEditorForm({title:'',content:'',read_time_minutes:3,sort_order:subChapters.length+1});setShowSubEditor(true);}}>
                      + Sub-chapter
                    </button>
                  )}
                </div>
                {subChapters.length === 0 && isSuperAdmin && (
                  <div style={{color:'#8A8A8A',fontSize:13,fontStyle:'italic'}}>
                    No sub-chapters yet. Click + Sub-chapter to add one.
                  </div>
                )}
                {subChapters.map((sc,i)=>(
                  <div key={sc.id} style={{border:'1px solid #E8E0D4',borderRadius:10,
                    marginBottom:12,overflow:'hidden'}}>
                    {/* Sub-chapter header */}
                    <div style={{background:sc.my_read?'#F0F7F1':'#F9F8F5',
                      padding:'14px 20px',display:'flex',justifyContent:'space-between',
                      alignItems:'center',cursor:'pointer'}}
                      onClick={()=>setSelectedSubChapter(selectedSubChapter?.id===sc.id?null:sc)}>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <span style={{fontSize:13,color:sc.my_read?'#2E6B3A':'#A8893D',fontWeight:700}}>
                          {sc.my_read?'✓':'○'}
                        </span>
                        <div>
                          <div style={{fontSize:13,fontWeight:600,color:'#2C1F16'}}>{sc.title}</div>
                          <div style={{fontSize:11,color:'#8A8A8A'}}>
                            ⏱ {sc.read_time_minutes} min
                            {sc.status==='draft' && isSuperAdmin && (
                              <span style={{marginLeft:8,fontSize:9,background:'#FFF3DC',
                                color:'#8B5E0A',padding:'2px 6px',borderRadius:10,fontWeight:700}}>DRAFT</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        {isSuperAdmin && (
                          <>
                            <button className="btn btn-ghost btn-sm"
                              onClick={e=>{e.stopPropagation();setEditingSubChapter(sc);setSubEditorForm({title:sc.title,content:sc.content||'',read_time_minutes:sc.read_time_minutes,sort_order:sc.sort_order});setShowSubEditor(true);}}>
                              Edit
                            </button>
                            {sc.status==='draft'
                              ? <button className="btn btn-outline btn-sm"
                                  onClick={e=>{e.stopPropagation();put('/bloans-standard/sub-chapters/'+sc.id+'/publish',{}).then(()=>get('/bloans-standard/chapters/'+selectedChapter.id+'/sub-chapters').then(d=>setSubChapters(d.data||[])));}}>
                                  Publish
                                </button>
                              : <button className="btn btn-ghost btn-sm" style={{color:'#8B5E0A'}}
                                  onClick={e=>{e.stopPropagation();put('/bloans-standard/sub-chapters/'+sc.id+'/unpublish',{}).then(()=>get('/bloans-standard/chapters/'+selectedChapter.id+'/sub-chapters').then(d=>setSubChapters(d.data||[])));}}>
                                  Unpublish
                                </button>
                            }
                            <button className="btn btn-ghost btn-sm" style={{color:'#8F2E24'}}
                              onClick={e=>{e.stopPropagation();if(window.confirm('Delete this sub-chapter?'))del('/bloans-standard/sub-chapters/'+sc.id).then(()=>get('/bloans-standard/chapters/'+selectedChapter.id+'/sub-chapters').then(d=>setSubChapters(d.data||[])));}}>
                              Delete
                            </button>
                          </>
                        )}
                        <span style={{color:'#A8893D',fontSize:12,transform:selectedSubChapter?.id===sc.id?'rotate(90deg)':'rotate(0)',transition:'transform .2s'}}>▶</span>
                      </div>
                    </div>
                    {/* Sub-chapter content */}
                    {selectedSubChapter?.id===sc.id && (
                      <div style={{padding:'20px 24px',borderTop:'1px solid #E8E0D4',background:'#fff'}}>
                        <div style={{lineHeight:1.9,fontSize:14,color:'#2C1F16',
                          fontFamily:'Georgia,serif',marginBottom:16}}
                          dangerouslySetInnerHTML={{__html:sc.content||'<p style="color:#8A8A8A;font-style:italic">No content yet.</p>'}}
                        />
                        {sc.status==='published' && !sc.my_read && (
                          <button onClick={()=>markSubChapterRead(sc.id)}
                            style={{background:'#556B2F',color:'#fff',border:'none',
                              borderRadius:6,padding:'8px 20px',fontSize:12,cursor:'pointer'}}>
                            Mark as Read
                          </button>
                        )}
                        {sc.my_read && (
                          <div style={{fontSize:12,color:'#2E6B3A',fontWeight:600}}>✓ Read</div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Acknowledgement */}
            {selectedChapter.status==='published' && (
              <div style={{background:selectedChapter.my_ack?'#F0F7F1':'#FBF8F3',
                border:'1px solid '+(selectedChapter.my_ack?'#B8DEC0':'#D4C5A9'),
                borderRadius:12,padding:24,textAlign:'center',marginTop:20}}>
                {selectedChapter.my_ack ? (
                  <div>
                    <div style={{fontSize:24,marginBottom:8}}>✅</div>
                    <div style={{fontSize:13,fontWeight:600,color:'#2E6B3A'}}>
                      Acknowledged on {new Date(selectedChapter.my_ack).toLocaleDateString('en-NG',{day:'numeric',month:'long',year:'numeric'})}
                    </div>
                    <div style={{fontSize:11,color:'#8A8A8A',marginTop:4}}>
                      Your acknowledgement is recorded.
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{fontSize:13,color:'#3B2A21',marginBottom:4,fontFamily:'Georgia,serif'}}>
                      I have read and understood this chapter.
                    </div>
                    <div style={{fontSize:11,color:'#8A8A8A',marginBottom:16}}>
                      Your name, date, version and timestamp will be recorded.
                      {subChapters.filter(s=>s.status==='published').length > 0 && (
                        <span style={{display:'block',marginTop:4,color:subChapters.filter(s=>s.status==='published').every(s=>s.my_read)?'#2E6B3A':'#8B5E0A'}}>
                          {subChapters.filter(s=>s.status==='published').every(s=>s.my_read)
                            ? '✓ All sub-chapters read'
                            : `Read all sub-chapters first (${subChapters.filter(s=>s.my_read).length}/${subChapters.filter(s=>s.status==='published').length} read)`}
                        </span>
                      )}
                    </div>
                    <button onClick={()=>acknowledge(selectedChapter.id)}
                      style={{background:'#2C1F16',color:'#C9A96E',border:'none',
                        borderRadius:8,padding:'10px 28px',fontSize:13,cursor:'pointer',
                        fontFamily:'Georgia,serif',letterSpacing:'.02em'}}>
                      Acknowledge
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── CHAPTER EDITOR MODAL ─────────────────────────────────────── */}
      {showEditor && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:820,width:'92vw'}}>
            <div className="modal-hdr">
              <span className="modal-title">{editingChapter?'Edit Chapter':'New Chapter'}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowEditor(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'80vh',overflowY:'auto'}}>
              <div className="form-row">
                <div className="form-group" style={{flex:3}}>
                  <label className="form-label">Chapter Title *</label>
                  <input className="form-control" value={editorForm.title}
                    onChange={e=>setEditorForm({...editorForm,title:e.target.value})}
                    placeholder="e.g. We Protect the Standard" />
                </div>
                <div className="form-group">
                  <label className="form-label">Read Time (mins)</label>
                  <input type="number" className="form-control" value={editorForm.read_time_minutes}
                    onChange={e=>setEditorForm({...editorForm,read_time_minutes:parseInt(e.target.value)||5})} min="1" />
                </div>
                <div className="form-group">
                  <label className="form-label">Sort Order</label>
                  <input type="number" className="form-control" value={editorForm.sort_order}
                    onChange={e=>setEditorForm({...editorForm,sort_order:parseInt(e.target.value)||0})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Tags (comma separated)</label>
                  <input className="form-control" value={editorForm.tags}
                    onChange={e=>setEditorForm({...editorForm,tags:e.target.value})}
                    placeholder="e.g. Attendance, Leadership, Finance" />
                </div>
                <div className="form-group" style={{display:'flex',alignItems:'center',paddingTop:24}}>
                  <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13}}>
                    <input type="checkbox" checked={editorForm.is_mandatory}
                      onChange={e=>setEditorForm({...editorForm,is_mandatory:e.target.checked})}
                      style={{width:16,height:16,accentColor:'#556B2F'}} />
                    Required Reading
                  </label>
                </div>
              </div>
              {editingChapter && (
                <div className="form-group">
                  <label className="form-label">Version Notes (what changed)</label>
                  <input className="form-control" value={editorForm.version_notes}
                    onChange={e=>setEditorForm({...editorForm,version_notes:e.target.value})}
                    placeholder="e.g. Updated overtime approval process" />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Content</label>
                <RichEditor value={editorForm.content}
                  onChange={val=>setEditorForm({...editorForm,content:val})}
                  placeholder="Write chapter content here..." />
              </div>

              {/* Sub-chapters inside editor */}
              <div style={{marginTop:24,borderTop:'1px solid #E8E0D4',paddingTop:20}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#556B2F',
                    textTransform:'uppercase',letterSpacing:'.1em'}}>
                    Sub-chapters ({editorSubChapters.length})
                  </div>
                  <button type="button" className="btn btn-outline btn-sm"
                    onClick={()=>{setEditingEditorSub(null);setEditorSubForm({title:'',content:'',read_time_minutes:3});setShowEditorSubForm(true);}}>
                    + Add Sub-chapter
                  </button>
                </div>

                {/* Existing sub-chapters list */}
                {editorSubChapters.map((sc,i)=>(
                  <div key={sc.id||i} style={{background:'#F9F8F5',border:'1px solid #E8E0D4',
                    borderRadius:8,padding:'10px 14px',marginBottom:8,
                    display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:'#2C1F16'}}>{sc.title}</div>
                      <div style={{fontSize:11,color:'#8A8A8A'}}>⏱ {sc.read_time_minutes} min</div>
                    </div>
                    <div style={{display:'flex',gap:6}}>
                      <button type="button" className="btn btn-ghost btn-sm"
                        onClick={()=>{setEditingEditorSub(sc);setEditorSubForm({title:sc.title,content:sc.content||'',read_time_minutes:sc.read_time_minutes});setShowEditorSubForm(true);}}>
                        Edit
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm" style={{color:'#8F2E24'}}
                        onClick={()=>setEditorSubChapters(prev=>prev.filter((_,idx)=>idx!==i))}>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}

                {/* Inline sub-chapter form */}
                {showEditorSubForm && (
                  <div style={{background:'#fff',border:'1px solid #A8893D',
                    borderRadius:8,padding:16,marginTop:8}}>
                    <div style={{fontSize:12,fontWeight:600,color:'#3B2A21',marginBottom:12}}>
                      {editingEditorSub?'Edit Sub-chapter':'New Sub-chapter'}
                    </div>
                    <div className="form-row">
                      <div className="form-group" style={{flex:3}}>
                        <label className="form-label">Title *</label>
                        <input className="form-control" value={editorSubForm.title}
                          onChange={e=>setEditorSubForm({...editorSubForm,title:e.target.value})}
                          placeholder="Sub-chapter title" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Read Time</label>
                        <input type="number" className="form-control" value={editorSubForm.read_time_minutes}
                          onChange={e=>setEditorSubForm({...editorSubForm,read_time_minutes:parseInt(e.target.value)||3})} min="1" />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Content</label>
                      <RichEditor value={editorSubForm.content}
                        onChange={val=>setEditorSubForm({...editorSubForm,content:val})}
                        placeholder="Write sub-chapter content..." />
                    </div>
                    <div style={{display:'flex',gap:8,marginTop:8}}>
                      <button type="button" className="btn btn-outline btn-sm"
                        onClick={()=>{setShowEditorSubForm(false);setEditingEditorSub(null);}}>
                        Cancel
                      </button>
                      <button type="button" className="btn btn-primary btn-sm"
                        onClick={()=>{
                          if (!editorSubForm.title) { alert('Title required'); return; }
                          if (editingEditorSub) {
                            setEditorSubChapters(prev=>prev.map(s=>s===editingEditorSub?{...s,...editorSubForm}:s));
                          } else {
                            setEditorSubChapters(prev=>[...prev,{...editorSubForm,sort_order:prev.length+1,status:'draft'}]);
                          }
                          setShowEditorSubForm(false);
                          setEditingEditorSub(null);
                          setEditorSubForm({title:'',content:'',read_time_minutes:3});
                        }}>
                        {editingEditorSub?'Update':'Add'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowEditor(false)}>Cancel</button>
              <button className="btn btn-outline btn-sm" style={{color:'#8B5E0A'}}
                onClick={saveChapter} disabled={saving}>
                {saving?'Saving...':'Save as Draft'}
              </button>
              <button className="btn btn-primary" disabled={saving}
                onClick={async()=>{
                  const ok = await saveChapter();
                  if (ok && editingChapter) publishChapter(editingChapter.id);
                }}>
                {saving?'Saving...':'Save & Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SUB-CHAPTER EDITOR MODAL ────────────────────────────────── */}
      {showSubEditor && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:820,width:'92vw'}}>
            <div className="modal-hdr">
              <span className="modal-title">{editingSubChapter?'Edit Sub-chapter':'New Sub-chapter'}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowSubEditor(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'80vh',overflowY:'auto'}}>
              <div className="form-row">
                <div className="form-group" style={{flex:3}}>
                  <label className="form-label">Sub-chapter Title *</label>
                  <input className="form-control" value={subEditorForm.title}
                    onChange={e=>setSubEditorForm({...subEditorForm,title:e.target.value})}
                    placeholder="e.g. Our Founding Purpose" />
                </div>
                <div className="form-group">
                  <label className="form-label">Read Time (mins)</label>
                  <input type="number" className="form-control" value={subEditorForm.read_time_minutes}
                    onChange={e=>setSubEditorForm({...subEditorForm,read_time_minutes:parseInt(e.target.value)||3})} min="1" />
                </div>
                <div className="form-group">
                  <label className="form-label">Sort Order</label>
                  <input type="number" className="form-control" value={subEditorForm.sort_order}
                    onChange={e=>setSubEditorForm({...subEditorForm,sort_order:parseInt(e.target.value)||0})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Content</label>
                <RichEditor value={subEditorForm.content}
                  onChange={val=>setSubEditorForm({...subEditorForm,content:val})}
                  placeholder="Write sub-chapter content here..." />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowSubEditor(false)}>Cancel</button>
              <button className="btn btn-outline btn-sm" style={{color:'#8B5E0A'}}
                onClick={saveSubChapter} disabled={saving}>
                {saving?'Saving...':'Save as Draft'}
              </button>
              <button className="btn btn-primary" disabled={saving}
                onClick={async()=>{
                  const ok = await saveSubChapter();
                  if (ok && editingSubChapter) {
                    await put('/bloans-standard/sub-chapters/'+editingSubChapter.id+'/publish',{});
                    get('/bloans-standard/chapters/'+selectedChapter.id+'/sub-chapters').then(d=>setSubChapters(d.data||[]));
                  }
                }}>
                {saving?'Saving...':'Save & Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SECTION EDITOR MODAL ─────────────────────────────────────── */}
      {showSectionEditor && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:480}}>
            <div className="modal-hdr">
              <span className="modal-title">{editingSection?'Edit Section':'New Section'}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowSectionEditor(false)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group" style={{flex:3}}>
                  <label className="form-label">Title *</label>
                  <input className="form-control" value={sectionForm.title}
                    onChange={e=>setSectionForm({...sectionForm,title:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Icon</label>
                  <input className="form-control" value={sectionForm.icon}
                    onChange={e=>setSectionForm({...sectionForm,icon:e.target.value})}
                    style={{fontSize:20,textAlign:'center'}} maxLength={2} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={2} value={sectionForm.description}
                  onChange={e=>setSectionForm({...sectionForm,description:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Sort Order</label>
                <input type="number" className="form-control" value={sectionForm.sort_order}
                  onChange={e=>setSectionForm({...sectionForm,sort_order:parseInt(e.target.value)||0})} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowSectionEditor(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveSection} disabled={saving}>
                {saving?'Saving...':editingSection?'Save Changes':'Create Section'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
